import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import {
  DollarSign,
  ShoppingCart,
  MapPin,
  TrendingUp,
  BarChart3,
  Target,
  Map,
  LayoutDashboard,
  RefreshCw,
} from "lucide-react";
import BusinessIntelligence from "./BusinessIntelligence";
import SalesTargets from "./SalesTargets";
import TerritoryManagement from "./TerritoryManagement";

import { API_BASE_URL } from "../config";

const CRM = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);

  // Dashboard summary data
  const [dashboardData, setDashboardData] = useState({
    totalSales: 0,
    totalOrders: 0,
    activeTerritories: 0,
    salesTargets: 0,
    targetAchievement: 0,
  });

  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchDashboardData();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch summary data for dashboard
      const [salesRes, ordersRes, territoriesRes, targetsRes] =
        await Promise.all([
          axios.get(`${API_BASE_URL}/api/sales/summary`, { headers }),
          axios.get(`${API_BASE_URL}/api/orders/summary`, { headers }),
          axios.get(`${API_BASE_URL}/api/territories/summary`, { headers }),
          axios.get(`${API_BASE_URL}/api/sales-targets/summary`, { headers }),
        ]);

      setDashboardData({
        totalSales: salesRes.data.totalSales || 0,
        totalOrders: ordersRes.data.totalOrders || 0,
        activeTerritories: territoriesRes.data.activeTerritories || 0,
        salesTargets: targetsRes.data.totalTargets || 0,
        targetAchievement: targetsRes.data.achievement || 0,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Don't show error toast for dashboard data as it's not critical
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            CRM Management
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Analytics, Sales Targets, and Territory Management
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? "..." : formatCurrency(dashboardData.totalSales)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Sales
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? "..." : dashboardData.totalOrders.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Orders
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? "..." : dashboardData.activeTerritories}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Territories
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loading
                  ? "..."
                  : formatPercentage(dashboardData.targetAchievement)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Achievement
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto">
            {[
              { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
              {
                id: "business-intelligence",
                label: "Business Intelligence",
                icon: BarChart3,
              },
              { id: "sales-targets", label: "Sales Targets", icon: Target },
              {
                id: "territory-management",
                label: "Territory Management",
                icon: Map,
              },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {/* Recent Activity */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        New sales report generated
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        2 hours ago
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Sales target updated for Q4
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        1 day ago
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        New territory assigned
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        3 days ago
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab("business-intelligence")}
                  className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      View Analytics
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Business Intelligence
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("sales-targets")}
                  className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                >
                  <Target className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Manage Targets
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Sales Targets
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("territory-management")}
                  className="flex items-center gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <Map className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Territories
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Territory Management
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Business Intelligence Tab */}
          {activeTab === "business-intelligence" && <BusinessIntelligence />}

          {/* Sales Targets Tab */}
          {activeTab === "sales-targets" && <SalesTargets />}

          {/* Territory Management Tab */}
          {activeTab === "territory-management" && <TerritoryManagement />}
        </div>
      </div>
    </div>
  );
};

export default CRM;
